import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

import Button from '../../components/Button';
import TextInput from '../../components/TextInput';
import '../Login/Login.css';

// Shared by the invite and password-reset flows — the one-time token in the URL
// records which one it is, and the page adjusts its wording accordingly.
// Mirrors the backend policy (app/core/password_policy.py) so the user gets
// instant feedback; the backend re-checks on submit.
const strongEnough = (pw) =>
  pw.length >= 12 &&
  /[a-z]/.test(pw) && /[A-Z]/.test(pw) && /\d/.test(pw) &&
  /[!@#$%^&*()\-_=+[\]{};:,.<>?/|\\~`"']/.test(pw);

const SetPasswordScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [checking, setChecking] = React.useState(true);
  const [info, setInfo] = React.useState(null);   // { valid, purpose, username, full_name }
  const [password, setPassword] = React.useState({ value: '', error: '' });
  const [confirm, setConfirm] = React.useState({ value: '', error: '' });
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    const check = async () => {
      if (!token) { setInfo({ valid: false }); setChecking(false); return; }
      try {
        const { data } = await axios.get('/api/auth/token-info', { params: { token } });
        if (active) setInfo(data);
      } catch (err) {
        if (active) setInfo({ valid: false });
      } finally {
        if (active) setChecking(false);
      }
    };
    check();
    return () => { active = false; };
  }, [token]);

  const isInvite = info && info.purpose === 'invite';
  const heading = isInvite ? 'Set your password' : 'Choose a new password';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!strongEnough(password.value)) {
      setPassword({ ...password, error: 'At least 12 characters with upper, lower, a digit and a special character.' });
      return;
    }
    if (confirm.value !== password.value) {
      setConfirm({ ...confirm, error: 'Passwords do not match.' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post('/api/auth/set-password', { token, new_password: password.value });
      toast.success(isInvite ? 'Your account is ready — please sign in.' : 'Password updated — please sign in.');
      navigate('/login', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not set your password. The link may have expired.';
      setPassword({ ...password, error: typeof msg === 'string' ? msg : 'Could not set your password.' });
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (checking) {
      return <p className='auth_helper_text'>Checking your link…</p>;
    }
    if (!info || !info.valid) {
      return (
        <div>
          <p className='auth_helper_text'>
            This link is invalid or has expired. Ask your administrator for a new invite,
            or request a fresh password-reset link.
          </p>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Button label='Request a reset link' style={{ alignSelf: 'flex-start' }}
              onClick={() => navigate('/forgot-password')} />
            <button type='button' className='auth_back_link' style={{ marginTop: 0 }}
              onClick={() => navigate('/login')}>Back to login</button>
          </div>
        </div>
      );
    }
    return (
      <form onSubmit={handleSubmit}>
        <p className='auth_helper_text'>
          {isInvite
            ? `Welcome${info.full_name ? ', ' + info.full_name : ''}. Choose a password to activate the account for `
            : `Choose a new password for `}
          <strong>{info.username}</strong>.
        </p>
        <TextInput
          type='password'
          label='New password'
          icon='lock'
          placeholder='New password'
          value={password.value}
          autoFocus
          onChange={(ev) => setPassword({ value: ev.target.value })}
          errorMessage={password.error}
        />
        <TextInput
          type='password'
          label='Confirm password'
          icon='lock'
          placeholder='Confirm password'
          value={confirm.value}
          onChange={(ev) => setConfirm({ value: ev.target.value })}
          onKeyDown={(ev) => { if (ev.key === 'Enter') handleSubmit(ev); }}
          errorMessage={confirm.error}
        />
        <Button
          type='submit'
          label={submitting ? 'Saving…' : (isInvite ? 'Activate account' : 'Update password')}
          disabled={submitting}
          style={{ alignSelf: 'flex-start' }}
          onClick={handleSubmit}
        />
      </form>
    );
  };

  return (
    <div className='loginContainer' style={{ backgroundImage: 'url(/assets/login-bg.jpg)' }}>
      <div className='blue_container' />
      <div className='login_form_container'>
        <div className='login_form_content'>
          <div className='login_form'>
            <h1 className='login_form_heading'>{heading}</h1>
            {renderBody()}
          </div>

          <div className='logo_container'>
            <img src='/assets/logo-new.png' alt='Frontline Logo' />
          </div>
        </div>
        <p className='powered_container'>
          Powered By
          <span>Watcher</span>
        </p>
      </div>
    </div>
  );
};

export default SetPasswordScreen;
